import React, { useEffect, useState } from "react"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
} from "reactstrap"
import { FaPlus, FaCopy } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import { Switch } from "antd"

const CustomerModal = ({
  open,
  toggle,
  selectedCustomer,
  setSelectedCustomer,
  viewOnly,
  onSubmit,
}) => {
  const [errors, setErrors] = useState({})
  const [members, setMembers] = useState([])

  useEffect(() => {
    setErrors({})
    setMembers(
      (selectedCustomer?.members || []).map(m => ({
        ...m,
        outstandingFee: m.outstandingFee ?? "0",
      }))
    )
  }, [open, selectedCustomer?.id])

  const handleChange = (field, value) => {
    setSelectedCustomer(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: "" }))
  }

  const addMember = () =>
    setMembers(prev => [
      ...prev,
      { id: Date.now(), name: "", outstandingFee: "0", _isNew: true },
    ])

  const removeMember = id =>
    setMembers(prev => prev.filter(m => m.id !== id))

  const updateMember = (id, value) =>
    setMembers(prev =>
      prev.map(m => (m.id === id ? { ...m, name: value } : m))
    )

  const updateMemberStatus = (id, status) =>
    setMembers(prev =>
      prev.map(m => (m.id === id ? { ...m, status } : m))
    )

  const updateMemberFee = (id, value) =>
    setMembers(prev =>
      prev.map(m => (m.id === id ? { ...m, outstandingFee: value } : m))
    )

  const validate = () => {
    const errs = {}
    if (!selectedCustomer?.customerName?.trim()) errs.customerName = "Customer name is required"
    if (!selectedCustomer?.phone?.trim())
      errs.phone = "Phone number is required"
    else if (!/^\d{10}$/.test(selectedCustomer.phone))
      errs.phone = "Phone must be 10 digits"
    if (!selectedCustomer?.alternatePhone?.trim())
      errs.alternatePhone = "WhatsApp number is required"
    else if (!/^\d{10}$/.test(selectedCustomer.alternatePhone))
      errs.alternatePhone = "WhatsApp number must be 10 digits"
    if (selectedCustomer?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(selectedCustomer.email))
        errs.email = "Invalid email address"
    }
    if (selectedCustomer?.aadharCard) {
      if (!/^\d{12}$/.test(selectedCustomer.aadharCard))
        errs.aadharCard = "Aadhar number must be 12 digits"
    }

    if (selectedCustomer?.panCard) {
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(selectedCustomer.panCard))
        errs.panCard = "Invalid PAN number"
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return


    const payload = {
      ...(selectedCustomer?.id && { id: selectedCustomer.id }),
      customerName: selectedCustomer?.customerName || "",
      email: selectedCustomer?.email || "",
      phone: selectedCustomer?.phone || "",
      alternatePhone: selectedCustomer?.alternatePhone || "",
      aadharCard: selectedCustomer?.aadharCard || "",
      panCard: selectedCustomer?.panCard || "",
      address: selectedCustomer?.address || "",
      feesPaid: selectedCustomer?.feesPaid === "yes" ? true : selectedCustomer?.feesPaid === "no" ? false : null,
      feesRemaining: selectedCustomer?.feesRemaining || "",
      members: members
        .filter(m => m.name.trim())
        .map(m =>
          m._isNew
            ? { name: m.name, outstandingFee: m.outstandingFee ?? "0" }
            : {
                id: m.id,
                name: m.name,
                status: m.status,
                outstandingFee: m.outstandingFee ?? "0",
              }
        ),
    }

    onSubmit(payload)
  }

  const renderError = field =>
    errors[field] && (
      <div className="invalid-feedback d-block">{errors[field]}</div>
    )

  const invalid = field => (errors[field] ? "is-invalid" : "")

  return (
    <Modal isOpen={open} centered size="xl" toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {viewOnly
          ? "View Customer"
          : selectedCustomer?.id
            ? "Edit Customer"
            : "Add Customer"}

        <div>
          {!viewOnly && (
              <Button className="modal-btn" onClick={handleSubmit}>
                {selectedCustomer?.id ? "Update" : "Submit"}
              </Button>
          )}
        </div>
      </ModalHeader>

      <ModalBody>
        <Form>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Customer Name <span className="text-danger">*</span>
                </Label>
                <Input
                  value={selectedCustomer?.customerName || ""}
                  onChange={e => handleChange("customerName", e.target.value)}
                  disabled={viewOnly}
                  placeholder="Enter customer name"
                  className={`rounded-4 ${invalid("customerName")}`}
                />
                {renderError("customerName")}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={selectedCustomer?.email || ""}
                  onChange={e => handleChange("email", e.target.value)}
                  disabled={viewOnly}
                  placeholder="Enter email address"
                  className={`rounded-4 ${invalid("email")}`}
                />
                {renderError("email")}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>
                  Phone Number <span className="text-danger">*</span>
                </Label>
                <Input
                  value={selectedCustomer?.phone || ""}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, "")
                    handleChange("phone", val)
                  }}
                  maxLength={10}
                  disabled={viewOnly}
                  placeholder="Enter 10-digit phone"
                  className={`rounded-4 ${invalid("phone")}`}
                />
                {renderError("phone")}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>WhatsApp Number <span className="text-danger">*</span></Label>
                <InputGroup>
                  <Input
                    value={selectedCustomer?.alternatePhone || ""}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, "")
                      handleChange("alternatePhone", val)
                    }}
                    maxLength={10}
                    disabled={viewOnly}
                    placeholder="Enter WhatsApp number"
                    className={`rounded-4 ${invalid("alternatePhone")}`}
                    style={
                      viewOnly
                        ? { borderRadius: "1rem" }
                        : { borderRadius: "1rem 0 0 1rem", borderRight: "none" }
                    }
                  />
                  {!viewOnly && (
                    <Button
                      type="button"
                      color="secondary"
                      outline
                      className="d-flex align-items-center gap-1"
                      style={{ borderRadius: "0 1rem 1rem 0", fontSize: 12, whiteSpace: "nowrap" }}
                      onClick={() =>
                        handleChange("alternatePhone", selectedCustomer?.phone || "")
                      }
                    >
                      <FaCopy size={12} /> Same as Phone
                    </Button>
                  )}
                  {renderError("alternatePhone")}
                </InputGroup>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Aadhar Card</Label>
                <Input
                  value={selectedCustomer?.aadharCard || ""}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, "")
                    handleChange("aadharCard", val)
                  }}
                  maxLength={12}
                  disabled={viewOnly}
                  placeholder="Enter 12-digit Aadhar number"
                  className={`rounded-4 ${invalid("aadharCard")}`}
                />
                {renderError("aadharCard")}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>PAN Card</Label>
                <Input
                  value={selectedCustomer?.panCard || ""}
                  onChange={e =>
                    handleChange("panCard", e.target.value.toUpperCase())
                  }
                  maxLength={10}
                  disabled={viewOnly}
                  placeholder="Enter PAN number"
                  className={`rounded-4 ${invalid("panCard")}`}
                />
                {renderError("panCard")}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Address</Label>
                <Input
                  type="text"
                  value={selectedCustomer?.address || ""}
                  onChange={e => handleChange("address", e.target.value)}
                  disabled={viewOnly}
                  placeholder="Enter full address"
                  className="rounded-4"
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label>Fees Paid</Label>
                <Input
                  type="select"
                  value={
                    selectedCustomer?.feesPaid === true ? "yes"
                    : selectedCustomer?.feesPaid === false ? "no"
                    : selectedCustomer?.feesPaid || ""
                  }
                  onChange={e => {
                    const val = e.target.value
                    handleChange("feesPaid", val)
                    if (val === "yes") {
                      handleChange("feesRemaining", "0")
                    }
                  }}
                  disabled={viewOnly}
                  className="rounded-4"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </Input>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label>Fees Remaining</Label>
                <Input
                  type="text"
                  value={selectedCustomer?.feesRemaining || ""}
                  onChange={e => {
                    const val = e.target.value
                    if (/^\d*\.?\d*$/.test(val)) handleChange("feesRemaining", val)
                  }}
                  disabled={viewOnly || selectedCustomer?.feesPaid === "yes" || selectedCustomer?.feesPaid === true}
                  placeholder="Enter fees remaining"
                  className="rounded-4"
                />
              </FormGroup>
            </Col>

            {/* Members Section */}
            <Col md={12}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Label className="mb-0 fw-bold">
                  Members
                  <span
                    className="text-muted ms-2"
                    style={{ fontSize: 12, fontWeight: 400 }}
                  >
                    (Add family/business members under this customer)
                  </span>
                </Label>
                <div className="d-flex align-items-center gap-3">
                  {!viewOnly && (
                    <Button
                      type="button"
                      size="sm"
                      className="btn-blue rounded-4 d-flex align-items-center gap-1"
                      onClick={addMember}
                    >
                      <FaPlus size={12} /> Add Member
                    </Button>
                  )}
                </div>
              </div>

              {members.length > 0 ? (
                <div
                  style={{
                    background: "rgba(0,0,0,0.03)",
                    borderRadius: 10,
                    padding: 16,
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2 mb-1"
                    style={{ fontSize: 12, fontWeight: 600, color: "#6c757d" }}
                  >
                    <div style={{ minWidth: 32 }} />
                    <div style={{ width: "60%" }}>Member Name</div>
                    <div style={{ width: "25%" }}>Outstanding Fee</div>
                    {members.some(m => !m._isNew) && (
                      <div style={{ minWidth: 44, textAlign: "center" }}>
                        Status
                      </div>
                    )}
                    {!viewOnly && <div style={{ width: 30, margin: "0px 20px" }} />}
                  </div>
                  {members.map((member, idx) => (
                    <div
                      className="d-flex align-items-center gap-2 mb-2"
                      key={member.id}
                    >
                      <div
                        style={{
                          minWidth: 32,
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#556ee6",
                        }}
                      >
                        #{idx + 1}
                      </div>
                      <Input
                        value={member.name}
                        onChange={e =>
                          updateMember(member.id, e.target.value)
                        }
                        disabled={viewOnly}
                        placeholder={`Member ${idx + 1} name`}
                        className="rounded-4"
                        style={{ width: "60%" }}
                      />
                      <Input
                        type="text"
                        value={member.outstandingFee ?? "0"}
                        onChange={e =>
                          updateMemberFee(member.id, e.target.value)
                        }
                        disabled={viewOnly}
                        placeholder="Outstanding Fee"
                        className="rounded-4"
                        style={{ width: "25%" }}
                      />

                      {!member._isNew && (
                        <Switch
                          checked={member.status ?? true}
                          disabled={viewOnly}
                          onChange={checked => updateMemberStatus(member.id, checked)}
                        />
                      )}

                      {!viewOnly && (
                        <Button
                          type="button"
                          color="danger"
                          size="sm"
                          className="rounded-circle d-flex align-items-center justify-content-center border-0"
                          style={{ width: 30, height: 30, padding: 0, margin: "0px 20px" }}
                          onClick={() => removeMember(member.id)}
                        >
                          <MdDelete size={15} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !viewOnly && (
                  <div
                    style={{
                      border: "2px dashed rgba(0,0,0,0.12)",
                      borderRadius: 10,
                      padding: "16px",
                      textAlign: "center",
                      color: "#999",
                      fontSize: 13,
                    }}
                  >
                    Click &quot;Add Member&quot; to add family/business members
                  </div>
                )
              )}
            </Col>
          </Row>
        </Form>
      </ModalBody>

      {/* {!viewOnly && (
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            {selectedCustomer?.id ? "Update" : "Submit"}
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      )} */}
    </Modal>
  )
}

export default CustomerModal
